import { useState } from "react";
import { Link } from "react-router-dom";
import { MOCK_USERS, MOCK_ACTIVITIES, MOCK_VENUES, MOCK_PULSE } from "@/lib/mockData";
import { Users, Activity, MapPin, Coins, ArrowLeft, Shield } from "lucide-react";

const TABS = ["Overview", "Users", "Activities", "Venues", "Deals", "Check-ins"];

const Admin = () => {
  const [tab, setTab] = useState("Overview");

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-page">
      <header className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-heading font-black text-xl tracking-tighter">GRABB / ADMIN</span>
          </Link>
          <span className="brutal-border bg-[#FFCC00] rounded-full px-3 py-1 text-xs font-bold uppercase flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> Internal
          </span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI icon={Users} label="Total users" value="14,238" />
          <KPI icon={Activity} label="Active invites" value={MOCK_ACTIVITIES.length * 250} />
          <KPI icon={MapPin} label="Venues" value={MOCK_VENUES.length * 30} />
          <KPI icon={Coins} label="Tokens issued" value="2.1M" />
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`brutal-border rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap ${
                tab === t ? "bg-[#0A0A0A] text-white brutal-shadow-sm" : "bg-white"
              }`}
              data-testid={`admin-tab-${t.toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && (
          <div className="grid md:grid-cols-2 gap-4">
            <Card title="Recent signals">
              <Row k="Joins / 5min" v={MOCK_PULSE.recent_joins_5min} />
              <Row k="New activities / 10min" v={MOCK_PULSE.new_activities_10min} />
              <Row k="Live people" v={MOCK_PULSE.available_now} />
              <Row k="Within 10km" v={MOCK_PULSE.nearby_count_10km} />
            </Card>
            <Card title="Quick triggers">
              <Trigger label="Run commit checks" />
              <Trigger label="Run auto-cancellations" />
              <Trigger label="Send post-activity check-ins" />
              <Trigger label="Process re-engagement" />
            </Card>
          </div>
        )}

        {tab === "Users" && (
          <Card title="Users">
            <Table
              cols={["Name", "Suburb", "Tokens", "Actions"]}
              rows={MOCK_USERS.map((u) => [
                u.name + ", " + u.age,
                u.suburb,
                "245",
                <ActionGroup key={u.id} id={u.id} />,
              ])}
            />
          </Card>
        )}

        {tab === "Activities" && (
          <Card title="Activities">
            <Table
              cols={["Title", "Host", "Type", "Spots"]}
              rows={MOCK_ACTIVITIES.map((a) => [
                a.title,
                a.host.name,
                a.type,
                `${a.participants.length}/${a.max_participants}`,
              ])}
            />
          </Card>
        )}

        {tab === "Venues" && (
          <Card title="Venues">
            <Table
              cols={["Name", "Category", "Distance", "Deal"]}
              rows={MOCK_VENUES.map((v) => [v.name, v.category, `${v.distance_km} km`, v.deal?.label || "—"])}
            />
          </Card>
        )}

        {tab === "Deals" && (
          <Card title="Deals">
            <Table
              cols={["Venue", "Label", "Status"]}
              rows={MOCK_VENUES.filter((v) => v.deal).map((v) => [v.name, v.deal.label, "Active"])}
            />
          </Card>
        )}

        {tab === "Check-ins" && (
          <Card title="Venue check-ins">
            <Table
              cols={["User", "Venue", "Time"]}
              rows={[
                ["Mia", "Reuben Hills", "2m ago"],
                ["Sam", "Gumshara Ramen", "15m ago"],
                ["Riley", "F45 Surry Hills", "1h ago"],
              ]}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value }) => (
  <div className="brutal-card p-4">
    <Icon className="w-4 h-4 mb-2" />
    <div className="font-heading font-black text-2xl leading-none">{value}</div>
    <div className="label-mono text-[#555555] mt-1">{label}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="brutal-card p-5">
    <h3 className="label-mono mb-3">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between text-sm border-b border-[#E0E0E0] pb-2 last:border-0 last:pb-0">
    <span className="text-[#555555]">{k}</span>
    <span className="font-mono font-bold">{v}</span>
  </div>
);

const Trigger = ({ label }) => (
  <button className="w-full brutal-border bg-white rounded-lg p-3 text-left text-sm font-bold flex items-center justify-between" data-testid={`admin-trigger-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    {label} <span className="text-xs text-[#555555]">→</span>
  </button>
);

const Table = ({ cols, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left">
          {cols.map((c) => (
            <th key={c} className="label-mono pb-2 text-[#555555] border-b border-[#E0E0E0]">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-[#E0E0E0] last:border-0">
            {row.map((cell, j) => (
              <td key={j} className="py-3 pr-3">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ActionGroup = ({ id }) => (
  <div className="flex gap-1">
    <button className="brutal-border bg-white px-2 py-1 text-xs font-bold rounded" data-testid={`admin-block-${id}`}>Block</button>
    <button className="brutal-border bg-[#FF6A2C] text-white px-2 py-1 text-xs font-bold rounded" data-testid={`admin-delete-${id}`}>Delete</button>
  </div>
);

export default Admin;
